/**
 * Hook for submitting ALI discrepancy reports
 */
import { useState } from 'react';
import { useFetchRetry } from '@rsos/capstone';
import { useCurrentPSAP } from '@rsos/reduxDataStore/src/features/capstone/psapInfo';
import { useSelectedCallerID } from '@rsos/reduxDataStore/src/features/emergencyCalls';
import { useCurrentUser } from '@rsos/sinatra/src/selectors';
import { getAPIHost } from '@rsos/utils/metaTags';
import useCallDetails from '../JurisdictionView/EventPane/useCallDetails';

/**
 * Available values for the `dtype` field from the Discrep Fields INdigital API
 * 20250604-1.pdf file from INdigital.
 */
export const DISCREPANCY_TYPE_OPTIONS = [
  { value: 'ALIincorrect', label: 'ALI Incorrect' },
  { value: 'Misroute', label: 'Misroute' },
  { value: 'ANI', label: 'ANI' },
  { value: 'ChangeRequest', label: 'Change Request' },
  { value: 'MSAGrequest', label: 'MSAG Request' },
  { value: 'GIS', label: 'GIS' },
  { value: 'LVF', label: 'LVF' },
  { value: 'NRF', label: 'NRF' },
  { value: 'Other', label: 'Other' },
] as const;

export const DISCREPANCY_TYPE_DEFAULT = DISCREPANCY_TYPE_OPTIONS[0];
export type DiscrepancyType =
  (typeof DISCREPANCY_TYPE_OPTIONS)[number]['value'];

export interface AliDiscrepancyFormData {
  discrepancy_type: DiscrepancyType;
  call_type: string;
  original_telephone_number: string;
  original_name: string;
  original_address: string;
  original_community: string;
  original_esn: string;
  corrected_telephone_number: string;
  corrected_name: string;
  corrected_address: string;
  corrected_community: string;
  corrected_esn: string;
  comments: string;
  // Raw ALI spill for roundtrip (not displayed in form).
  // NOTE: As of 2026-02-10 this is re-assembled from the more structured EIDO
  //   data we deal with in ali_ingres. By round-tripping it, if/when we get
  //   access to the raw ALI data, we can replace this without changing anything
  //   on the frontend.
  raw_ali: string;
}

interface UseFormSubmitReturn {
  submitDiscrepancy: (formData: AliDiscrepancyFormData) => Promise<boolean>;
  error: string | null;
  success: boolean;
}

export interface DiscrepancySubmitResponse {
  success: boolean;
  discrepancy_id?: string;
  message?: string;
  error?: string;
}

export interface DiscrepancySubmitPayload {
  caller_id: string;
  account_id: string;
  discrepancy_type: DiscrepancyType;
  call_type?: string;
  original_telephone_number: string;
  original_name?: string;
  original_address: string;
  original_community?: string;
  original_esn?: string;
  corrected_telephone_number?: string;
  corrected_name?: string;
  corrected_address?: string;
  corrected_community?: string;
  corrected_esn?: string;
  comments?: string;
  call_timestamp?: string;
  raw_ali?: string;
}

export const useFormSubmit = (): UseFormSubmitReturn => {
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Get current PSAP and call data using proper selectors
  const currentPSAP = useCurrentPSAP();
  const currentUser = useCurrentUser();
  const selectedCallerID = useSelectedCallerID();
  const callDetails = useCallDetails(selectedCallerID || '');
  const callTimestamp = callDetails?.lastTimestamp
    ? new Date(callDetails.lastTimestamp)
    : null;
  const fetchRetry = useFetchRetry({ shouldThrowResponse: false });

  const orgName = currentUser?.currentOrg?.name;
  if (!orgName) {
    throw new Error("Current user's organization name not available");
  }

  const submitDiscrepancy = async (
    formData: AliDiscrepancyFormData,
  ): Promise<boolean> => {
    setError(null);
    setSuccess(false);

    try {
      if (!currentPSAP?.account_id) {
        throw new Error('PSAP account ID not available');
      }

      if (!selectedCallerID) {
        throw new Error('No call selected');
      }

      // Build payload
      const payload: DiscrepancySubmitPayload = {
        caller_id: selectedCallerID,
        account_id: currentPSAP.account_id,
        discrepancy_type: formData.discrepancy_type,
        call_type: formData.call_type || undefined,
        original_telephone_number: formData.original_telephone_number,
        original_name: formData.original_name || undefined,
        original_address: formData.original_address,
        original_community: formData.original_community || undefined,
        original_esn: formData.original_esn || undefined,
        corrected_telephone_number:
          formData.corrected_telephone_number || undefined,
        corrected_name: formData.corrected_name || undefined,
        corrected_address: formData.corrected_address || undefined,
        corrected_community: formData.corrected_community || undefined,
        corrected_esn: formData.corrected_esn || undefined,
        comments: formData.comments || undefined,
        call_timestamp: callTimestamp?.toISOString() || undefined,
        raw_ali: formData.raw_ali || undefined,
      };

      // Submit to backend
      const apiHost = getAPIHost();
      const url = `${apiHost}/v1/ali/discrepancies`;

      const response = await fetchRetry(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-RapidSOS-Org': orgName,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.detail?.error ||
            `Failed to submit discrepancy: ${response.status}`,
        );
      }

      const result: DiscrepancySubmitResponse = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to submit discrepancy report');
      }

      setSuccess(true);
      return true;
    } catch (err) {
      // eslint-disable-next-line no-console -- log error for sentry logging
      console.error('Discrepancy submission failed', err);
      const errorMessage =
        err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      return false;
    }
  };

  return {
    submitDiscrepancy,
    error,
    success,
  };
};
